import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { getStats, getOrgs } from '../utils/api';
import { formatDistanceToNow } from '../utils/helpers';
import {
  Building2, Users, Briefcase, TrendingUp,
  CheckCircle, AlertTriangle, Clock, XCircle,
} from 'lucide-react';

// Defined outside component so React doesn't remount it on every render
function StatCard({ label, value, color, icon, sub }) {
  return (
    <div className="stat-card" style={{ '--stat-color': color }}>
      <span className="stat-label">
        {React.cloneElement(icon, { size: 11, style: { marginRight: 5, verticalAlign: '-1px' } })}
        {label}
      </span>
      <span className="stat-value" style={{ color }}>{value ?? '—'}</span>
      {sub && <span className="stat-sub">{sub}</span>}
    </div>
  );
}

export default function DashboardPage() {
  const [stats,   setStats]   = useState(null);
  const [orgs,    setOrgs]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getStats(), getOrgs()])
      .then(([s, o]) => {
        setStats(s);
        setOrgs(o.organizations || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const suspended  = orgs.filter(o => !o.is_active);
  const trialing   = orgs.filter(o => o.subscription_plan === 'trial' || o.subscription_plan === 'trialing' || o.subscription_status === 'trialing');
  const pastDue    = orgs.filter(o => o.subscription_status === 'past_due');
  const trialExpired = orgs.filter(o => {
    const isTrial = o.subscription_plan === 'trial' || o.subscription_plan === 'trialing' || o.subscription_status === 'trialing';
    return isTrial && o.trial_ends_at && new Date() > new Date(o.trial_ends_at);
  });

  const recent = [...orgs]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 8);

  if (loading) {
    return (
      <Layout title="Dashboard">
        <div className="loading-center">
          <div className="spinner" /> Loading platform data…
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Dashboard">

      {/* ── KPI row ── */}
      <div className="stat-grid">
        <StatCard
          label="Total Firms"
          value={stats?.total_orgs}
          color="var(--gold)"
          icon={<Building2 />}
          sub={`${stats?.active_orgs || 0} active`}
        />
        <StatCard
          label="Total Users"
          value={stats?.total_users}
          color="var(--blue)"
          icon={<Users />}
          sub="active accounts"
        />
        <StatCard
          label="Total Cases"
          value={stats?.total_cases}
          color="var(--purple)"
          icon={<Briefcase />}
        />
        <StatCard
          label="Trialing"
          value={trialing.length}
          color="var(--amber)"
          icon={<Clock />}
          sub={`${trialExpired.length} expired`}
        />
      </div>

      {/* ── Alert row ── */}
      {(suspended.length > 0 || pastDue.length > 0 || trialExpired.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
          {suspended.length > 0 && (
            <div style={{ background: 'var(--red-dim)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--r-lg)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <XCircle size={16} style={{ color: 'var(--red)', flexShrink: 0 }} />
              <div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', fontWeight: 700, color: 'var(--red)' }}>{suspended.length} SUSPENDED</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  {suspended.map(o => o.name).join(', ')}
                </div>
              </div>
            </div>
          )}
          {pastDue.length > 0 && (
            <div style={{ background: 'var(--amber-dim)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 'var(--r-lg)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <AlertTriangle size={16} style={{ color: 'var(--amber)', flexShrink: 0 }} />
              <div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', fontWeight: 700, color: 'var(--amber)' }}>{pastDue.length} PAST DUE</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  {pastDue.map(o => o.name).join(', ')}
                </div>
              </div>
            </div>
          )}
          {trialExpired.length > 0 && (
            <div style={{ background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.25)', borderRadius: 'var(--r-lg)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <Clock size={16} style={{ color: 'var(--purple)', flexShrink: 0 }} />
              <div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', fontWeight: 700, color: 'var(--purple)' }}>{trialExpired.length} TRIAL EXPIRED</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  {trialExpired.map(o => o.name).join(', ')}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Recent firms table ── */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Recently Joined Firms</span>
          <span className="mono-sm">{orgs.length} total</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Firm</th>
                <th>Owner</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Users</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {recent.map(org => (
                <OrgRow key={org.id} org={org} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

function OrgRow({ org }) {
  const navigate = useNavigate();
  const { planBadge, statusBadge } = useBadges(org);
  const daysLeft = org.trial_ends_at
    ? Math.ceil((new Date(org.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <tr style={{ cursor: 'pointer' }} onClick={() => navigate(`/orgs/${org.id}`)}>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className={`pulse-dot ${!org.is_active ? 'red' : org.subscription_status === 'past_due' ? 'amber' : 'green'}`} />
          <span style={{ fontWeight: 600 }}>{org.name}</span>
        </div>
      </td>
      <td>
        <div style={{ fontSize: '0.8rem' }}>{org.owner_name}</div>
        <div className="mono-sm">{org.owner_email}</div>
      </td>
      <td>{planBadge}</td>
      <td>
        {statusBadge}
        {(org.subscription_plan === 'trial' || org.subscription_plan === 'trialing' || org.subscription_status === 'trialing') && daysLeft !== null && (
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6rem', color: daysLeft <= 0 ? 'var(--red)' : daysLeft <= 3 ? 'var(--amber)' : 'var(--text-muted)', marginTop: 3 }}>
            {daysLeft <= 0 ? 'expired' : `${daysLeft}d left`}
          </div>
        )}
      </td>
      <td className="mono">{org.user_count}</td>
      <td className="mono-sm">{formatDistanceToNow(org.created_at)}</td>
    </tr>
  );
}

function useBadges(org) {
  const planBadge = {
    trial:      <span className="badge badge-blue">Trial</span>,
    trialing:   <span className="badge badge-blue">Trial</span>,
    early_bird: <span className="badge badge-green">Early Bird</span>,
    core:       <span className="badge badge-dim">Core</span>,
    pro:        <span className="badge badge-gold">Pro</span>,
    elite:      <span className="badge badge-purple">Elite</span>,
    // legacy
    free:       <span className="badge badge-dim">Free</span>,
    starter:    <span className="badge badge-dim">Starter</span>,
    enterprise: <span className="badge badge-purple">Enterprise</span>,
  }[org.subscription_plan] || <span className="badge badge-dim">{org.plan_name || org.subscription_plan?.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase()) || '—'}</span>;

  const statusBadge = !org.is_active
    ? <span className="badge badge-red">Suspended</span>
    : {
        active:    <span className="badge badge-green">Active</span>,
        trialing:  <span className="badge badge-blue">Trial</span>,
        past_due:  <span className="badge badge-amber">Past Due</span>,
        cancelled: <span className="badge badge-dim">Cancelled</span>,
      }[org.subscription_status] || <span className="badge badge-dim">{org.subscription_status}</span>;

  return { planBadge, statusBadge };
}
